import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory } from "react-router-dom";

import Card from "../../../Components/PagesComponents/AgentStatus/Card/Card";
import CustomSelect from "../../../Components/common/CustomSelect/CustomSelect";
import Modal from "../../../Components/PagesComponents/AgentStatus/Modal/Modal";
import ListPopup from "../../../Components/PagesComponents/Dashboard/ListPopup/ListPopup";

import ComputerIcon from "../../../Components/Icon-SVG/ComputerIcon";
import WindowsIcon from "../../../Components/Icon-SVG/WindowsIcon";
import TableSpinnerIcon from "../../../Components/Icon-SVG/TableSpinnerIcon";

import {
  RequestAgentInstallationList,
  RequestUpdateAgentDescription,
} from "../../../store/actions/integrationsActions";

import "./styles.scss";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

export const FILTER_BY_TIME = [
  { id: "7-day", value: "7 days" },
  { id: "30-day", value: "30 days" },
  { id: "all-all", value: "Show all" },
];

const filteringHeader = [
  { id: "none", value: "Sort Options..." },
  { id: "hostname", value: "Host Name" },
  { id: "username", value: "User Name" },
  { id: "macAddress", value: "Mac Address" },
  { id: "localIp", value: "Local IP" },
];

const onlineStatusOptions = [
  { id: "online", value: "Show Online" },
  { id: "offline", value: "Show Offline" },
  { id: "all", value: "Show All" },
];

// Default: "1 day"
const DEFAULT_TIME_FILTER = FILTER_BY_TIME.find((f) => f.id === "30-day");

const AgentStatus = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const query = useQuery();

  const [searchTerm, setSearchTerm] = useState("");
  const [editingCardId, setEditingCardId] = useState(null);
  const [sortField, setSortField] = useState(filteringHeader[0]);
  const [onlineFilter, setOnlineFilter] = useState(onlineStatusOptions[0]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState(DEFAULT_TIME_FILTER);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [visibleCount, setVisibleCount] = useState(20);

  const agentData = useSelector((state) => state.integrations.agentInstallationList);

  const data = query.get("data");
  const clientID = query.get("client_id");
  const cpuID = query.get("cpu_id");
  const hostname = query.get("hostname");
  const macAddress = query.get("macaddress");

  const isListPopupActive = !!data;
  const isModalActive = !!(clientID || cpuID || hostname || macAddress);

  useEffect(() => {
    if (selectedTimeFilter) {
      dispatch(RequestAgentInstallationList({ lastUpdatedTimeFilter: selectedTimeFilter }));
    }
  }, [selectedTimeFilter, dispatch]);

  useEffect(() => {
    setVisibleCount(20); // Filtreleme değişince başa dön
  }, [searchTerm, sortField, onlineFilter, selectedTimeFilter]);

  const handleRefresh = useCallback(() => {
    if (selectedTimeFilter) {
      setIsRefreshing(true);
      dispatch(RequestAgentInstallationList({ lastUpdatedTimeFilter: selectedTimeFilter }));
      setTimeout(() => setIsRefreshing(false), 1000);
    }
  }, [selectedTimeFilter, dispatch]);

  const handleDescriptionChange = (agentId, newDescription) => {
    dispatch(RequestUpdateAgentDescription({ agentId, newDescription }));
  };

  const filteredAgentData = [...agentData]
    .sort((a, b) => {
      if (sortField.id !== "none" && a[sortField.id] && b[sortField.id]) {
        return a[sortField.id].localeCompare(b[sortField.id]);
      }
      return 0;
    })
    .filter((agent) => {
      const searchFields = [agent.hostname, agent.username, agent.macAddress, agent.localIp];
      const matchesSearch = searchFields.some((field) =>
        field?.toLowerCase().includes(searchTerm.toLowerCase()),
      );

      const matchesOnline =
        onlineFilter.id === "all"
          ? true
          : onlineFilter.id === "online"
            ? agent.onlineStatus === true
            : agent.onlineStatus === false;

      return matchesSearch && matchesOnline;
    });

  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && visibleCount < filteredAgentData.length) {
          setVisibleCount((prev) => prev + 20); // 20'şer 20'şer artır
        }
      });

      if (node) observer.current.observe(node);
    },
    [visibleCount, filteredAgentData.length],
  );

  const handleCardClick = (agent) => {
    history.push(
      `/agentstatus?${agent.clientID ? "client_id=" + agent.clientID : ""}&${
        agent.cpuID ? "cpu_id=" + agent.cpuID : ""
      }&${agent.hostname ? "hostname=" + agent.hostname : ""}&${
        agent.macAddress ? "macaddress=" + agent.macAddress : ""
      }`,
    );
  };

  return (
    <div>
      <div className="agent">
        <div className="agent__header">
          <h1>All Agents</h1>
          <p>You can monitor the agent information connected to the current server</p>

          <div className="search-sort-wrapper">
            <input
              type="text"
              placeholder="Host Search..."
              value={searchTerm}
              style={{ marginRight: "2em", width: "220px" }}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            {/* Sort by field */}
            <div className="sort-dropdown">
              <div className="sort-dropdown__item">
                <CustomSelect
                  width="152px"
                  height="32px"
                  data={filteringHeader}
                  select={sortField}
                  setSelected={setSortField}
                />
              </div>
            </div>

            <div className="agent__toolbar-separator" />

            <div className="sort-dropdown">
              <div className="sort-dropdown__item">
                <CustomSelect
                  width="148px"
                  height="32px"
                  data={onlineStatusOptions}
                  select={onlineFilter}
                  setSelected={setOnlineFilter}
                />
              </div>
            </div>

            <div className="agent__toolbar-separator" />

            {/* Time filter */}
            <div className="sort-dropdown">
              <div className="sort-dropdown__item">
                <CustomSelect
                  width="120px"
                  height="32px"
                  data={FILTER_BY_TIME}
                  select={selectedTimeFilter}
                  setSelected={setSelectedTimeFilter}
                />
              </div>
            </div>

            {/* Refresh */}
            <div className="agent__refresh-btn" onClick={handleRefresh} title="Refresh agent list">
              <TableSpinnerIcon
                className="container-services__spinner"
                customStyle={{ marginLeft: "2px", height: "20px" }}
                spin={isRefreshing}
              />
            </div>
          </div>
        </div>

        <div className="agent__body">
          {filteredAgentData.slice(0, visibleCount).map((agent) => (
            <Card
              key={agent.agent_id}
              localIp={agent.localIp}
              hostName={agent.hostname}
              userName={agent.username}
              macAdress={agent.macAddress}
              status={agent.onlineStatus ? "Online" : "Offline"}
              updateTime={agent.updateTime}
              icon={
                <ComputerIcon
                  width={36}
                  height={36}
                  grayscale={agent.onlineStatus ? false : true}
                />
              }
              icon1={<WindowsIcon width={20} height={20} />}
              onClick={() => handleCardClick(agent)}
              description={agent.descClient}
              isEditing={editingCardId === agent.macAddress}
              onEditClick={() => setEditingCardId(agent.macAddress)}
              onCancelEdit={() => setEditingCardId(null)}
              onDescriptionChange={(newDesc) => {
                handleDescriptionChange(agent.agent_id, newDesc);
                setEditingCardId(null);
              }}
            />
          ))}
          {visibleCount < filteredAgentData.length && (
            <div ref={lastElementRef} style={{ height: "20px", margin: "10px" }}></div>
          )}
        </div>

        {isModalActive && <Modal selectedAgent={{ clientID, cpuID, hostname, macAddress }} />}
      </div>

      <ListPopup
        show={isListPopupActive}
        groupID={query.get("data")}
        tableType={query.get("table")}
        tableType2="agent"
      />
    </div>
  );
};

export default AgentStatus;
